import * as React from 'react';
import * as ReactDOM from 'react-dom';

export interface ISlideOverProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    widthPx?: number; // default 520
    className?: string; // Custom CSS class for the main container
    children: React.ReactNode;
}

export const SlideOver: React.FC<ISlideOverProps> = ({ open, onClose, title = 'Logga in', widthPx = 520, className, children }) => {
    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const lastFocused = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
        if (!open) {
            return;
        }

        // Keep focus inside dialog and close on ESC
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }

            if (e.key === 'Tab') {
                const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
                    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
                );
                if (!focusables || focusables.length === 0) {
                    return;
                }
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement as HTMLElement | null;

                if (e.shiftKey && active === first) {
                    last.focus();
                    e.preventDefault();
                } else if (!e.shiftKey && active === last) {
                    first.focus();
                    e.preventDefault();
                }
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    // Handle scroll lock and initial focus
    React.useEffect(() => {
        if (open) {
            lastFocused.current = document.activeElement as HTMLElement | null;
            document.body.style.overflow = 'hidden';
            setTimeout(() => panelRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus(), 0);
        } else {
            document.body.style.overflow = '';
            lastFocused.current?.focus?.();
        }
    }, [open]);

    if (!open) {
        return null;
    }

    const drawerStyle: React.CSSProperties & Record<'--drawer-w', string> = {
        ['--drawer-w']: `${widthPx}px`
    };

    return ReactDOM.createPortal(
        <>
            <div className='gh-slide-over__backdrop' aria-hidden='true' onClick={onClose} />

            {/* Själva slideovern från höger */}
            <aside role='dialog' aria-modal='true' aria-label={title} className={className || 'gh-slide-over'} style={drawerStyle}>
                <div ref={panelRef} className='gh-slide-over__inner'>
                    <header className='gh-slide-over__header'>
                        <h2 className='gh-slide-over__title'>{title}</h2>
                        <button type='button' onClick={onClose} aria-label='Stäng' className='gh-slide-over__close' data-autofocus>
                            <span>✕</span>
                        </button>
                    </header>

                    <div className='gh-slide-over__body'>{children}</div>
                </div>
            </aside>
        </>,
        document.body
    );
};
